const dateString = (date: Date): string => {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};

function getDaysBetweenDates(dates?: {
  startDate: Date;
  endDate: Date;
}): number {
  if (dates == null) {
    return 15;
  }
  const oneDay = 24 * 60 * 60 * 1000; // number of milliseconds in a day
  const { startDate, endDate } = dates;
  const diffInMs = Math.abs(endDate.getTime() - startDate.getTime()); // difference in milliseconds
  return Math.round(diffInMs / oneDay); // round to nearest integer
}

function getDataPoints({
  range,
  dates,
  callBack
}: {
  range: [number, number];
  dates: { startDate: Date; endDate: Date };
  callBack: any;
}): Array<{ date: string; value: number }> {
  const [start, end] = range;

  const numDays = getDaysBetweenDates(dates);

  const dataPoints: Array<{ date: string; value: number }> = [];
  const yValues = callBack(numDays, start, end);
  const labels = getDateLabels(numDays, dates);
  yValues.forEach((y: number, i: number) => {
    dataPoints.push({ date: labels[i], value: y });
  });
  return dataPoints;
}
export function getLogDataPoints({
  range,
  dates
}: {
  range: [number, number];
  dates: { startDate: Date; endDate: Date };
}): Array<{ date: string; value: number }> {
  return getDataPoints({ range, dates, callBack: getLogValues });
}
export function getSinDataPoints({
  range,
  dates
}: {
  range: [number, number];
  dates: { startDate: Date; endDate: Date };
}): Array<{ date: string; value: number }> {
  return getDataPoints({ range, dates, callBack: getSinValues });
}

export function getRandomDataPoints({
  range,
  dates = {
    startDate: new Date(new Date().getTime() - 8 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  }
}: {
  range: [number, number];
  dates: { startDate: Date; endDate: Date };
}): Array<{ date: string; value: number }> {
  return getDataPoints({ range, dates, callBack: getRandomValues });
}

const getLogValues = (
  numDays: number,
  start: number,
  end: number
): number[] => {
  const logStart = Math.log10(start);
  const logEnd = Math.log10(end);
  const step = (logEnd - logStart) / (numDays - 1);
  const yValues: number[] = [];
  for (let i = 0; i < numDays; i++) {
    const x = logStart + step * i;
    const y = Math.pow(10, x);
    yValues.push(y);
  }
  return yValues;
};
const getSinValues = (
  numDays: number,
  start: number,
  end: number
): number[] => {
  const step = (2 * Math.PI) / numDays;
  const yValues: number[] = [];
  for (let i = 0; i < numDays; i++) {
    const x = i * step;
    const y = Math.sin(x) * ((end - start) / 2) + (end + start) / 2;
    yValues.push(y);
  }
  return yValues;
};

const getRandomValues = (
  numDays: number,
  start: number,
  end: number
): number[] => {
  const yValues: number[] = [];
  for (let i = 0; i < numDays; i++) {
    const y = Math.floor(Math.random() * (end - start + 1)) + start;
    yValues.push(y);
  }
  return yValues;
};

const getDateLabels = (
  numDays: number,
  dates: { startDate: Date; endDate: Date }
): string[] => {
  const labels = [];
  for (let i = 0; i < numDays; i++) {
    const date = new Date(dates.startDate.getTime() + i * 24 * 60 * 60 * 1000);
    labels.push(dateString(date));
  }
  return labels;
};

export const getRandomDataPointsWithMetrics = (
  metrics: number[],
  range: [number, number]
): any => {
  const [start, end] = range;
  const dataPoints: Array<{ metric: number; value: number }> = [];

  metrics.forEach((metric: number) => {
    const value = Math.floor(Math.random() * (end - start + 1)) + start;
    dataPoints.push({ metric, value });
  });
  return dataPoints;
};

export const getFixedDataPointsWithMetrics = (
  metrics: number[],
  range: [number, number]
): any => {
  const [start, end] = range;
  const dataPoints: Array<{ metric: number; value: number }> = [];

  metrics.forEach((metric: number) => {
    const value = 10;
    dataPoints.push({ metric, value });
  });
  return dataPoints;
};

export function getStaticDataPoints({
  data
}: {
  data: any[];
}): Array<{ date: string; value: number }> {
  const dates = {
    startDate: new Date(
      new Date().getTime() - data.length * 24 * 60 * 60 * 1000
    ),
    endDate: new Date()
  };
  const numDays = getDaysBetweenDates(dates);
  const labels = getDateLabels(numDays, dates);
  const dataPoints: Array<{ date: string; value: number }> = [];
  data.forEach((y: number, i: number) => {
    dataPoints.push({ date: labels[i], value: y });
  });
  return dataPoints;
}
